import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { toast } from 'sonner';
import { Plus, CheckCircle, Eye, Ban, AlertCircle, Clock, CheckCircle2, TrendingUp } from 'lucide-react'; // Ícones adicionados para os KPIs
import DetalhesVendaModal from '../../components/DetalhesVendaModal'; 
import { getParamsForAPI, staticDateRange } from '@/services/FinanceiroService'; 
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ContasAReceberView() {
  const navigate = useNavigate();
  const [contasReceber, setContasReceber] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState("PENDENTE");
  
  // --- NOVO: Estado para os KPIs ---
  const [kpis, setKpis] = useState({
    atrasadas: { valor: 0, qtd: 0 },
    aVencer: { valor: 0, qtd: 0 },
    recebidas: { valor: 0, qtd: 0 },
    previsto: { valor: 0, qtd: 0 }
  });
  
  const [filters, setFilters] = useState({ date: staticDateRange });
  
  // Estado para Liquidação
  const [modalLiquidacao, setModalLiquidacao] = useState({
      isOpen: false,
      lancamento: null,
      valor: '',
      data: new Date().toISOString().split('T')[0]
  });

  // Estado para Cancelamento
  const [modalCancelamento, setModalCancelamento] = useState({
      isOpen: false,
      lancamento: null
  });

  // Estados para Detalhes
  const [isDetalhesVendaOpen, setIsDetalhesVendaOpen] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);

  // Helper para verificar atraso (Ajustado para aceitar status ATRASADO também)
  const verificarAtraso = (dataVencimento, status) => {
    if ((status !== 'PENDENTE' && status !== 'ATRASADO') || !dataVencimento) return false;
    return isBefore(parseISO(dataVencimento), startOfDay(new Date()));
  };
  
  // --- Funções de Carregamento ---
  const loadContasAReceber = async () => {
    setLoading(true);
    try {
      const params = getParamsForAPI(filters);
      
      const queryParams = { ...params, tipo: "ENTRADA" };
      if (filtroStatus === "ATRASADO") {
          queryParams.status = "ATRASADO"; 
      } else {
          queryParams.status = filtroStatus;
      }

      const res = await axiosInstance.get('/lancamentos', { params: queryParams });
      setContasReceber((res.data || []).sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento)));
    } catch (error) {
      toast.error('Erro ao carregar contas a receber.');
    } finally {
      setLoading(false);
    }
  };
  
  // --- NOVO: Função para carregar e calcular todos os KPIs ---
  const loadKPIs = async () => {
    try {
      const params = getParamsForAPI(filters);
      // Busca TODAS as entradas do período para calcular os totais
      const queryParams = { ...params, tipo: "ENTRADA" };
      const res = await axiosInstance.get('/lancamentos', { params: queryParams });
      const data = res.data || [];

      let calcAtrasadas = { valor: 0, qtd: 0 };
      let calcAVencer = { valor: 0, qtd: 0 };
      let calcRecebidas = { valor: 0, qtd: 0 };

      data.forEach(item => {
        if (item.status === 'RECEBIDO') {
          calcRecebidas.valor += item.valor;
          calcRecebidas.qtd += 1;
        } else if (item.status === 'PENDENTE' || item.status === 'ATRASADO') {
          // Verifica se está atrasado usando a data
          if (verificarAtraso(item.data_vencimento, 'PENDENTE') || item.status === 'ATRASADO') {
            calcAtrasadas.valor += item.valor;
            calcAtrasadas.qtd += 1;
          } else {
            calcAVencer.valor += item.valor;
            calcAVencer.qtd += 1;
          }
        }
      });

      setKpis({
        atrasadas: calcAtrasadas,
        aVencer: calcAVencer,
        recebidas: calcRecebidas,
        previsto: {
          valor: calcAtrasadas.valor + calcAVencer.valor,
          qtd: calcAtrasadas.qtd + calcAVencer.qtd
        }
      });
    } catch (error) {
      console.error("Erro ao carregar KPIs:", error);
    }
  };

  useEffect(() => {
    loadContasAReceber();
    loadKPIs();
  }, [filters, filtroStatus]);
  
  const handleFiltrarClick = () => {
    loadContasAReceber();
    loadKPIs();
  }
  
  // --- Handlers de Liquidação ---
  const handleOpenModalLiquidacao = (lancamento) => {
    setModalLiquidacao({
        isOpen: true,
        lancamento: lancamento,
        valor: lancamento.valor.toFixed(2),
        data: new Date().toISOString().split('T')[0]
    });
  };
  
  const handleCloseModalLiquidacao = () => {
    setModalLiquidacao({ isOpen: false, lancamento: null, valor: '', data: '' });
  };
  
  const handleLiquidarLancamento = async (e) => {
    e.preventDefault();
    const { lancamento, valor, data } = modalLiquidacao;
    const valorFloat = parseFloat(valor);

    if (!valorFloat || valorFloat <= 0) {
        toast.error("O valor a liquidar deve ser maior que zero.");
        return;
    }
    if (valorFloat > (lancamento.valor + 0.01)) { 
        toast.error(`Valor a liquidar (R$ ${valorFloat}) não pode ser maior que o valor pendente.`);
        return;
    }
    
    try {
      await axiosInstance.put(`/lancamentos/${lancamento.id}/liquidar`, {
          valor_liquidado: valorFloat,
          data_liquidacao: data
      });
      toast.success("Lançamento liquidado!");
      handleCloseModalLiquidacao();
      handleFiltrarClick(); 
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao liquidar lançamento.");
    }
  };

  // --- Handlers de Cancelamento ---
  const handleOpenModalCancelamento = (lancamento) => {
    setModalCancelamento({
        isOpen: true,
        lancamento: lancamento
    });
  };

  const handleConfirmarCancelamento = async () => {
    if (!modalCancelamento.lancamento) return;
    try {
        await axiosInstance.put(`/lancamentos/${modalCancelamento.lancamento.id}/cancelar`);
        toast.success("Conta cancelada com sucesso!");
        setModalCancelamento({ isOpen: false, lancamento: null });
        handleFiltrarClick(); 
    } catch (error) {
        toast.error("Erro ao cancelar lançamento.");
    }
  };

  // Handler para "Ver Detalhes"
  const handleShowDetails = async (item) => {
    if (!item.venda_id) {
      toast.info("Este é um lançamento avulso e não possui uma venda vinculada.");
      return;
    }
    setLoadingModal(true);
    setIsDetalhesVendaOpen(true);
    try {
      const res = await axiosInstance.get(`/vendas/${item.venda_id}`);
      setSelectedVenda(res.data);
    } catch (error) {
      toast.error("Erro ao buscar detalhes da venda.");
      setIsDetalhesVendaOpen(false);
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Cabeçalho da Página */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Contas a Receber
        </h1>
        <p className="text-gray-600">Gerencie todos os recebimentos pendentes ou realizados.</p>
      </div>

      {/* --- NOVO: GRID DE KPIs --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        
        {/* Card: Atrasadas */}
        <div className="bg-white rounded-xl p-5 border border-red-200 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">Atrasadas</p>
              <h3 className="text-2xl font-bold text-red-700">
                R$ {kpis.atrasadas.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500"><strong>{kpis.atrasadas.qtd}</strong> parcela(s)</p>
        </div>

        {/* Card: A Vencer */}
        <div className="bg-white rounded-xl p-5 border border-yellow-200 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-sm font-medium text-yellow-600 mb-1">A Vencer</p>
              <h3 className="text-2xl font-bold text-yellow-700">
                R$ {kpis.aVencer.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500"><strong>{kpis.aVencer.qtd}</strong> parcela(s)</p>
        </div>

        {/* Card: Total Recebido */}
        <div className="bg-white rounded-xl p-5 border border-green-200 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Saldo Total (Recebidas)</p>
              <h3 className="text-2xl font-bold text-green-700">
                R$ {kpis.recebidas.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500"><strong>{kpis.recebidas.qtd}</strong> recebimento(s)</p>
        </div>

        {/* Card: Previsto */}
        <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Previsto</p>
              <h3 className="text-2xl font-bold text-blue-700">
                R$ {kpis.previsto.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500"><strong>{kpis.previsto.qtd}</strong> pendência(s) no total</p>
        </div>

      </div>
      {/* --------------------------- */}
      
      {/* Filtros */}
      <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Período (Vencimento)</Label>
            <DateRangePicker
              startDate={filters.date.from}
              endDate={filters.date.to}
              onChange={({ startDate, endDate }) => {
                setFilters({ 
                  ...filters, 
                  date: { 
                    from: startDate, 
                    to: endDate 
                  } 
                });
              }}
              placeholder="Selecione o período"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Filtrar Status</Label>
            <Select value={filtroStatus} onValueChange={(value) => setFiltroStatus(value)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDENTE">Pendentes</SelectItem>
                <SelectItem value="ATRASADO">Atrasados</SelectItem>
                <SelectItem value="RECEBIDO">Recebidos</SelectItem>
                <SelectItem value="CANCELADO">Cancelados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={handleFiltrarClick} className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              Filtrar
            </Button>
            <Button onClick={() => navigate('/financeiro/novo-recebimento')} style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#1a202c' }}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Receita
            </Button>
          </div>
        </div>
      </div>

      {/* Tabela de Lançamentos */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Lançamentos a Receber</h3>
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente / Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forma Pagto.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ação</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center p-6 text-gray-500">A carregar lançamentos...</td>
                </tr>
              ) : contasReceber.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center p-6 text-gray-500">
                    Nenhum lançamento {filtroStatus.toLowerCase()} encontrado para este período.
                  </td>
                </tr>
              ) : (
                contasReceber.map((p) => {
                   const isLate = verificarAtraso(p.data_vencimento, p.status);
                   
                   return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                         <p className="text-sm text-gray-900">{p.cliente_nome || p.pagador_beneficiario || 'N/A'}</p>
                         <p className="text-xs text-gray-500">{p.descricao}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{p.forma_pagamento}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                          {p.data_vencimento ? format(parseISO(p.data_vencimento), 'dd/MM/yyyy') : 'N/A'}
                          {isLate && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Atrasado
                            </span>
                          )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-center flex justify-center gap-2">
                        {p.status === "PENDENTE" && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleOpenModalLiquidacao(p)}
                              title="Receber"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleOpenModalCancelamento(p)}
                              title="Cancelar Conta"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleShowDetails(p)} title="Ver Detalhes" disabled={!p.venda_id}>
                           <Eye className={`w-4 h-4 ${p.venda_id ? 'text-blue-600' : 'text-gray-300'}`} />
                        </Button>
                      </td>
                    </tr>
                   );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal de Liquidação */}
      <Dialog open={modalLiquidacao.isOpen} onOpenChange={handleCloseModalLiquidacao}>
          <DialogContent>
              <DialogHeader>
                  <DialogTitle>Receber Conta</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleLiquidarLancamento} className="space-y-4">
                  <div>
                      <Label>Descrição</Label>
                      <Input value={modalLiquidacao.lancamento?.descricao || ''} disabled />
                  </div>
                  <div>
                      <Label>Valor Pendente</Label>
                      <Input value={`R$ ${(modalLiquidacao.lancamento?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} disabled />
                  </div>
                  <div>
                      <Label htmlFor="valor_liquidado">Valor a Receber (R$)</Label>
                      <Input 
                          id="valor_liquidado"
                          type="number" 
                          step="0.01"
                          value={modalLiquidacao.valor}
                          onChange={(e) => setModalLiquidacao(prev => ({...prev, valor: e.target.value}))}
                          required
                      />
                  </div>
                  <div>
                      <Label htmlFor="data_liquidacao">Data do Recebimento</Label>
                      <Input 
                          id="data_liquidacao"
                          type="date"
                          value={modalLiquidacao.data}
                          onChange={(e) => setModalLiquidacao(prev => ({...prev, data: e.target.value}))}
                          required
                      />
                  </div>
                  <Button type="submit" className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      Confirmar Recebimento
                  </Button>
              </form>
          </DialogContent>
      </Dialog>

      {/* Modal de Cancelamento */}
      <Dialog open={modalCancelamento.isOpen} onOpenChange={() => setModalCancelamento({isOpen: false, lancamento: null})}>
          <DialogContent>
              <DialogHeader>
                  <DialogTitle>Cancelar Conta</DialogTitle>
                  <DialogDescription>
                    Tem certeza que deseja cancelar esta conta a receber? <br/>
                    Ela deixará de aparecer como pendente e não afetará seu saldo.
                  </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                  <p className="font-medium">{modalCancelamento.lancamento?.descricao}</p>
                  <p className="text-sm text-gray-500">Valor: R$ {modalCancelamento.lancamento?.valor.toFixed(2)}</p>
              </div>
              <DialogFooter>
                  <Button variant="outline" onClick={() => setModalCancelamento({isOpen: false, lancamento: null})}>Voltar</Button>
                  <Button variant="destructive" onClick={handleConfirmarCancelamento}>Confirmar Cancelamento</Button>
              </DialogFooter>
          </DialogContent>
      </Dialog>
      
      {/* Modal de Detalhes da Venda */}
      {selectedVenda && (
        <DetalhesVendaModal
          venda={selectedVenda}
          isOpen={isDetalhesVendaOpen}
          onClose={() => {setIsDetalhesVendaOpen(false); setSelectedVenda(null);}}
          loading={loadingModal}
        />
      )}
    </div>
  );
}